/**
 * Block splitting for streamed markdown.
 *
 * Lives outside `MessageContent.tsx` so these pure helpers can be unit-tested
 * (and so exporting them doesn't break fast refresh for the component file).
 */
import { marked } from "marked";

/**
 * True when the tail of a streaming block is a markdown link whose closing
 * `)` hasn't arrived yet — i.e. `[label](https://…` with no matching close.
 *
 * Why this matters: a presigned S3 download URL streams in over many tokens,
 * and an unterminated `[label](url` is not yet link syntax, so the renderer
 * paints the half-arrived URL as raw text. The user watches a wall of
 * `X-Amz-Signature` query junk reflow on every token, then vanish when the
 * `)` finally lands. Holding the block back renders the link exactly once.
 *
 * Scanning from the end (rather than counting brackets across the block)
 * keeps this O(tail) and confines it to the only case that matters: an open
 * link at the very end of the in-progress block. A closed link earlier in the
 * block, or a stray `(`, both fall through without a match.
 */
function hasUnclosedTrailingLink(block: string): boolean {
  const linkUrlStart = block.lastIndexOf("](");
  if (linkUrlStart === -1) {
    return false;
  }
  // A `)` after `](` means that link already closed; whatever follows is
  // ordinary text, so nothing is pending.
  if (block.includes(")", linkUrlStart + 2)) {
    return false;
  }
  // Require the `[` that opens the label, otherwise `](` is incidental text.
  return block.lastIndexOf("[", linkUrlStart) !== -1;
}

/**
 * Count GFM cells in a pipe row. Leading `|` is required; a missing trailing
 * `|` still counts the mid-cell fragments so callers can compare against the
 * header width while treating the row as incomplete.
 */
function countTableRowCells(line: string): number {
  const trimmed = line.trim();
  if (!trimmed.startsWith("|")) {
    return 0;
  }
  const parts = trimmed.split("|");
  // "| A | B |" → ["", " A ", " B ", ""] → 2 cells
  // "| A | B"   → ["", " A ", " B"] → 2 fragments, still incomplete
  if (parts[parts.length - 1] === "") {
    return Math.max(0, parts.length - 2);
  }
  return Math.max(0, parts.length - 1);
}

function isTableSeparatorLine(line: string): boolean {
  return /^\|[\s:|-]+\|$/.test(line.trim());
}

/**
 * A table line is publishable once it closes with `|` and has at least as many
 * cells as the header. Mid-cell text (`| Austin | Clo`) and short rows are held.
 */
function isCompleteTableLine(line: string, headerCellCount: number): boolean {
  const trimmed = line.trim();
  if (!trimmed.startsWith("|") || !trimmed.endsWith("|")) {
    return false;
  }
  if (isTableSeparatorLine(trimmed)) {
    return headerCellCount === 0 || countTableRowCells(trimmed) >= headerCellCount;
  }
  return countTableRowCells(trimmed) >= headerCellCount;
}

/**
 * Pipe-only paragraphs are GFM tables that have not yet grown a complete
 * separator. `marked` keeps them as `paragraph` until `|---|---|` lands, so
 * rendering them paints raw pipes that suddenly become a `<table>` — the
 * one-shot flash at table start. Hold the whole block until it lexes as a table.
 *
 * Includes the partial first cell (`| Fac`) before its closing pipe arrives —
 * that is still a pipe-led paragraph, not ordinary prose.
 */
function isIncompleteTablePreamble(block: string): boolean {
  const lines = block.split("\n").filter((line) => line.trim().length > 0);
  if (lines.length === 0) {
    return false;
  }
  // Ordinary prose that merely mentions `|` mid-sentence does not start with `|`.
  return lines.every((line) => line.trim().startsWith("|"));
}

/**
 * Trim a trailing incomplete data row from a `table` token so the memoized
 * block string only changes when a full row lands. Without this, every
 * character (and every smooth-reveal frame) rewrites the entire `<table>`.
 *
 * Completed rows are normalized to end with `\n` so the token that appends the
 * row's trailing newline does not count as a second content change.
 */
function stabilizeStreamingTable(raw: string): string {
  // A trailing newline means every line above it already closed (row finished).
  // Without one, the final line may still be mid-cell / mid-row.
  const lastLineAlreadyClosed = raw.endsWith("\n");
  const closedLines = lastLineAlreadyClosed ? raw.slice(0, -1).split("\n") : raw.split("\n");

  if (closedLines.length < 2) {
    // Too short to be header+separator; just normalize the trailing newline.
    return lastLineAlreadyClosed ? raw : `${raw}\n`;
  }

  const headerCellCount = countTableRowCells(closedLines[0] ?? "");
  const trailingLine = closedLines[closedLines.length - 1] ?? "";
  const trailingLineIncomplete =
    !isCompleteTableLine(trailingLine, headerCellCount) || hasUnclosedTrailingLink(trailingLine);
  const publishableLines = trailingLineIncomplete ? closedLines.slice(0, -1) : closedLines;

  // Need header + separator at minimum; otherwise withhold entirely.
  if (publishableLines.length < 2) {
    return "";
  }
  return `${publishableLines.join("\n")}\n`;
}

/**
 * Uses `marked.lexer()` to split a markdown string into block-level tokens
 * (paragraphs, headings, code fences, lists, etc.) and returns their raw strings.
 *
 * This is the key to block-level memoization: when a new streaming token arrives,
 * the full content is re-tokenized, but completed blocks produce the exact same
 * raw string as the previous render. `MemoizedMarkdownBlock` uses a string equality
 * check to skip re-rendering those stable blocks.
 *
 * Note: the lexer itself is O(n) per call (it runs on the full string every time).
 * The win is in avoiding O(n) *rendering* for stable blocks — only the last
 * (actively streaming) block's React tree is rebuilt.
 *
 * The final block is withheld while it ends in an unterminated link, so a long
 * URL appears as a finished link rather than streaming in as raw text. While
 * `holdBackIncompleteTable` is on (live stream tail only), incomplete GFM table
 * preambles are withheld the same way, and in-progress table rows are trimmed
 * so the table only grows one completed row at a time. Finished messages pass
 * the flag off so a short last row is not dropped.
 */
export function parseMarkdownIntoBlocks(
  markdown: string,
  { holdBackIncompleteTable = false }: { holdBackIncompleteTable?: boolean } = {}
): string[] {
  const tokens = marked.lexer(markdown);
  const contentTokens = tokens.filter((token) => token.raw.trim().length > 0);
  const blocks: string[] = [];

  for (let i = 0; i < contentTokens.length; i += 1) {
    const token = contentTokens[i];
    if (token === undefined) {
      continue;
    }
    const isLast = i === contentTokens.length - 1;

    if (!isLast) {
      blocks.push(token.raw);
      continue;
    }

    // Only the final block can be mid-stream; earlier ones are already settled.
    // Tables must not use the whole-block link hold-back: a cell URL streaming
    // in would unmount the entire table chrome until `)` arrives. Incomplete
    // last rows (including ones with an open link) are trimmed below instead.
    if (token.type !== "table" && hasUnclosedTrailingLink(token.raw)) {
      break;
    }

    if (
      holdBackIncompleteTable &&
      token.type === "paragraph" &&
      isIncompleteTablePreamble(token.raw)
    ) {
      break;
    }

    if (token.type === "table") {
      if (!holdBackIncompleteTable) {
        blocks.push(token.raw);
        continue;
      }
      const stabilized = stabilizeStreamingTable(token.raw);
      if (stabilized.length > 0) {
        blocks.push(stabilized);
      }
      continue;
    }

    blocks.push(token.raw);
  }

  return blocks;
}
